import { useEffect, useRef } from "react";

const FireworksCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let cw = 0;
    let ch = 0;
    let requestId;

    /* ================= SIZE ================= */
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      cw = rect.width;
      ch = rect.height;
      canvas.width = cw;
      canvas.height = ch;
    };

    resizeCanvas();

    // const resizeObserver = new ResizeObserver(() => {
    //   requestAnimationFrame(resizeCanvas);
    // });

    // resizeObserver.observe(canvas.parentElement);

    /* ================= UTIL ================= */
    const random = (min, max) => Math.random() * (max - min) + min;
    const calculateDistance = (x1, y1, x2, y2) => Math.hypot(x1 - x2, y1 - y2);

    /* ================= COLOR PALETTE ================= */
    const COLOR_PALETTE = [
      [42, 55],
      [0, 10],
      [200, 220], // đỏ
      [260, 290],
      [160, 180],
      [0, 360],
    ];

    const randomHue = () => {
      const [min, max] =
        COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];
      return random(min, max);
    };

    /* ================= VARIABLES ================= */
    let fireworks = [];
    let particles = [];
    let limiterTotal = 20;
    let limiterTick = 0;
    let timerTotal = 500;
    let timerTick = 0;
    let mousedown = false;
    let mx = 0;
    let my = 0;

    /* ================= FIREWORK ================= */
    function Firework(sx, sy, tx, ty) {
      this.x = sx;
      this.y = sy;
      this.sx = sx;
      this.sy = sy;
      this.tx = tx;
      this.ty = ty;

      this.distanceToTarget = calculateDistance(sx, sy, tx, ty);
      this.distanceTraveled = 0;

      this.coordinates = [];
      this.coordinateCount = 3;
      while (this.coordinateCount--) this.coordinates.push([this.x, this.y]);

      this.angle = Math.atan2(ty - sy, tx - sx);
      this.speed = 2;
      this.acceleration = 1.05;
      this.brightness = random(55, 75);
      this.hue = randomHue();
    }

    Firework.prototype.update = function (index) {
      this.coordinates.pop();
      this.coordinates.unshift([this.x, this.y]);

      this.speed *= this.acceleration;
      const vx = Math.cos(this.angle) * this.speed;
      const vy = Math.sin(this.angle) * this.speed;

      this.distanceTraveled = calculateDistance(
        this.sx,
        this.sy,
        this.x + vx,
        this.y + vy
      );

      if (this.distanceTraveled >= this.distanceToTarget) {
        createParticles(this.tx, this.ty, this.hue);
        fireworks.splice(index, 1);
      } else {
        this.x += vx;
        this.y += vy;
      }
    };

    Firework.prototype.draw = function () {
      ctx.beginPath();
      ctx.moveTo(
        this.coordinates[this.coordinates.length - 1][0],
        this.coordinates[this.coordinates.length - 1][1]
      );
      ctx.lineTo(this.x, this.y);
      ctx.strokeStyle = `hsl(${this.hue},100%,${this.brightness}%)`;
      ctx.stroke();
    };

    /* ================= PARTICLE ================= */
    function Particle(x, y, hue) {
      this.x = x;
      this.y = y;

      this.coordinates = [];
      this.coordinateCount = 5;
      while (this.coordinateCount--) this.coordinates.push([this.x, this.y]);

      this.angle = random(0, Math.PI * 2);
      this.speed = random(2, 12);
      this.friction = 0.95;
      this.gravity = 0.6;

      this.hue = random(hue - 15, hue + 15);
      this.brightness = random(60, 85);
      this.alpha = 1;
      this.decay = random(0.01, 0.02);
    }

    Particle.prototype.update = function (index) {
      this.coordinates.pop();
      this.coordinates.unshift([this.x, this.y]);

      this.speed *= this.friction;
      this.x += Math.cos(this.angle) * this.speed;
      this.y += Math.sin(this.angle) * this.speed + this.gravity;

      this.alpha -= this.decay;
      if (this.alpha <= this.decay) particles.splice(index, 1);
    };

    Particle.prototype.draw = function () {
      ctx.beginPath();
      ctx.moveTo(
        this.coordinates[this.coordinates.length - 1][0],
        this.coordinates[this.coordinates.length - 1][1]
      );
      ctx.lineTo(this.x, this.y);
      ctx.strokeStyle = `hsla(${this.hue},100%,${this.brightness}%,${this.alpha})`;
      ctx.stroke();
    };

    const createParticles = (x, y, hue) => {
      let count = 30;
      while (count--) particles.push(new Particle(x, y, hue));
    };

    /* ================= MAIN LOOP ================= */
    const loop = () => {
      requestId = requestAnimationFrame(loop);

      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fillRect(0, 0, cw, ch);
      ctx.globalCompositeOperation = "lighter";

      for (let i = fireworks.length - 1; i >= 0; i--) {
        fireworks[i].draw();
        fireworks[i].update(i);
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        particles[i].draw();
        particles[i].update(i);
      }

      if (timerTick >= timerTotal) {
        timerTick = 0;
      } else {
        if (timerTick % 400 <= 15) {
          fireworks.push(
            new Firework(100, ch, random(180, 220), random(ch * 0.2, ch * 0.35))
          );
          fireworks.push(
            new Firework(
              cw - 100,
              ch,
              random(cw - 220, cw - 180),
              random(ch * 0.2, ch * 0.35)
            )
          );
        }

        // ================= RANDOM EXTRA SHOTS (LOW FREQUENCY) =================
        if (Math.random() < 0.03) {
          const extraShots = 1;

          const startX = random(cw * 0.35, cw * 0.65);

          fireworks.push(
            new Firework(
              startX,
              ch,
              startX + random(-50, 50),
              random(ch * 0.25, ch * 0.4)
            )
          );
        }

        timerTick++;
      }

      if (limiterTick >= limiterTotal) {
        if (mousedown) {
          fireworks.push(new Firework(cw / 2, ch, mx, my));
          limiterTick = 0;
        }
      } else {
        limiterTick++;
      }
    };

    /* ================= EVENTS ================= */
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mx = e.clientX - rect.left;
      my = e.clientY - rect.top;
    };

    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mousedown", () => (mousedown = true));
    window.addEventListener("mouseup", () => (mousedown = false));

    loop();

    return () => {
      cancelAnimationFrame(requestId);
      // resizeObserver.disconnect();
      canvas.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", () => (mousedown = false));
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full h-full block" />;
};

export default FireworksCanvas;
